/**
 * @file Measures an element's border box through a React 19 callback ref.
 * Consumers: `FormPage` (the Navb) and `FormPageContent` (the sticky header).
 * The ref callback seeds the size from `getBoundingClientRect` during commit, so
 * the first paint already carries it, then a ResizeObserver follows the element;
 * the ref's cleanup disconnects it. No `useEffect`, so it also works for an
 * element that mounts after an early return. An unchanged rect keeps the same
 * object, so an observer tick that moves nothing re-renders nothing.
 */
import { useCallback, useState } from 'react';

export type ElementBoxSize = { width: number; height: number };

export const useElementBoxSize = <T extends HTMLElement>(): [
  ref: (node: T | null) => (() => void) | undefined,
  size: ElementBoxSize | undefined,
] => {
  const [size, setSize] = useState<ElementBoxSize>();

  const ref = useCallback((node: T | null) => {
    if (!node) return undefined;
    const read = () => {
      const { width, height } = node.getBoundingClientRect();
      setSize((prev) =>
        prev?.width === width && prev?.height === height ? prev : { width, height },
      );
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, size];
};
